import clsx from "clsx";
import React, { forwardRef, useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";

import { KEY_MAPPING } from "../../utils/keyCodes";
import { svgIcon } from "../../utils/svgProvider";
import Loading from "../common/Loading";

import { ITEM_PROPERTIES } from "./Dropdown.utils";

interface DropdownProps {
	id: string;
	withSearch?: boolean;
	autoFocus?: boolean;
	isFetching?: boolean;
	className?: string;
	error?: string;
	ariaLabel?: string;
	label: string;
	placeholder?: string;
	disabled?: boolean;
	items: any[];
	selectedItem?: (item: any) => string;
	renderItem?: (item: any) => React.ReactElement;
	onChange: ({
		target,
		type,
		isTrusted,
	}: {
		target;
		type: string;
		isTrusted: boolean;
	}) => void;
}

export const Dropdown = forwardRef<HTMLInputElement, DropdownProps>(
	(props, ref) => {
		const [isActive, setIsActive] = useState(false);
		const [isTyping, setIsTyping] = useState(false);
		const [items, setItems] = useState(props?.items || []);
		const [query, setQuery] = useState("");

		const selectedItemRef = useRef(
			items?.find((item) => item?.isSelected) || null,
		);

		const listRef = useRef(null);

		const accessibilitySubject = `${props.id}-listbox-item-`;

		const renderOptionContent = (item) => {
			if (props?.renderItem) {
				return props.renderItem(item);
			} else {
				return (
					<>
						{item.key}{" "}
						{item.isSelected ? svgIcon.radioSelected : svgIcon.radioUnselected}
					</>
				);
			}
		};

		const handleExpandDropdown = () => {
			if (items?.length > 0) {
				if (isActive) {
					setItems(
						items?.map((item) => ({
							...item,
							isActive: item.value === selectedItemRef.current.value,
						})),
					);
				}

				setIsActive(!isActive);
			}
		};

		const changeItemProperty = ({
			item,
			delta,
			property,
		}: {
			item?: any;
			delta?: number;
			property: string;
		}) => {
			const activeItem = item || items.find((item) => item.itemIndex === delta);

			if (activeItem) {
				setItems(
					items.map((item) => ({
						...item,
						[property]:
							item.value === activeItem.value ||
							item.itemIndex === activeItem.itemIndex,
					})),
				);

				if (property === ITEM_PROPERTIES.IS_SELECTED) {
					selectedItemRef.current = activeItem;

					props.onChange({
						isTrusted: false,
						target: { id: props.id, name: props.id, value: activeItem.value },
						type: "select",
					});

					setIsActive(false);
					setIsTyping(false);
				}
			}
		};

		const handleItemClick = (item) => {
			if (props?.withSearch) {
				const value = props?.selectedItem(item) || item.key;
				setQuery(value);
			}

			changeItemProperty({ item, property: ITEM_PROPERTIES.IS_SELECTED });
		};

		const handleKeyDown = (event) => {
			const activeItemIndex = event.target.getAttribute("data-index");

			switch (event.key) {
				case KEY_MAPPING.ENTER.KEY:
					isActive
						? changeItemProperty({
								delta: activeItemIndex,
								property: ITEM_PROPERTIES.IS_SELECTED,
							})
						: setIsActive(true);
					break;
				case KEY_MAPPING.ESCAPE.KEY:
					setIsActive(false);
					break;

				case KEY_MAPPING.SPACE.KEY:
					break;
			}
		};

		const handleItemKeyDown = (event) => {
			const activeItemIndex = parseInt(
				event.target.getAttribute("data-index"),
				10,
			);

			switch (event.key) {
				case KEY_MAPPING.ENTER.KEY:
					isActive
						? changeItemProperty({
								delta: activeItemIndex,
								property: ITEM_PROPERTIES.IS_SELECTED,
							})
						: setIsActive(false);
					break;
				case KEY_MAPPING.TAB.KEY:
					event.shiftKey
						? changeItemProperty({
								delta: activeItemIndex - 1,
								property: ITEM_PROPERTIES.IS_ACTIVE,
							})
						: changeItemProperty({
								delta: activeItemIndex + 1,
								property: ITEM_PROPERTIES.IS_ACTIVE,
							});
					break;
			}
		};

		const handleQueryChange = (event) => {
			const value = event.target.value;
			if (!value.trim()) {
				setIsActive(false);
				setItems([]);
			}
			setQuery(value);
			setIsTyping(true);
		};

		// set lookup items
		useEffect(() => {
			if (!!props?.items?.length && props?.items?.length !== items?.length) {
				setIsActive(true);
				setItems(props.items);
				selectedItemRef.current = props?.items?.find((item) => item.isSelected);
			}
		}, [props?.items, items?.length]);

		// reset dropdown on go back
		useEffect(() => {
			if (!isActive && !isTyping && props?.items?.length === 0) {
				setQuery("");
			}
		}, [isActive, isTyping, props?.items?.length]);

		// no results found from lookup
		useEffect(() => {
			if (
				isActive &&
				props?.items.length === 0 &&
				items.length !== props?.items.length
			) {
				setItems([]);
				setIsActive(false);
			}

			return () => {
				if (!isActive && props?.items?.length === 0) {
					setItems([]);
					setIsActive(false);
				}
			};
		}, [isActive, props?.items?.length, items.length]);

		useEffect(() => {
			const handleClickOutside = (event) => {
				if (listRef.current && !listRef.current.contains(event.target)) {
					setIsActive(false);
					setIsTyping(false);
				}
			};

			document.addEventListener("mousedown", handleClickOutside);
			return () => {
				document.removeEventListener("mousedown", handleClickOutside);
			};
		}, []);

		return (
			<div
				id={props.id}
				ref={listRef}
				data-testid={`${props.id}-dropdown-wrapper`}
				className={clsx({
					[`${props.className}`]: true,
					dropdown: true,
					active: isActive,
					error: props.error,
					loading: props?.isFetching,
				})}
				aria-label={props.ariaLabel}
			>
				{!props?.withSearch && (
					<input
						id={props.id}
						ref={ref}
						autoComplete="new-password"
						type="hidden"
						tabIndex={-1}
						value={selectedItemRef.current?.value || ""}
					/>
				)}

				<label className="label" htmlFor={props.id}>
					{props.label}
				</label>

				{props?.withSearch ? (
					<input
						id={props.id}
						ref={ref}
						type="text"
						placeholder={props?.placeholder}
						aria-haspopup="listbox"
						disabled={props?.disabled}
						autoFocus={props?.autoFocus}
						autoComplete="new-password"
						data-testid={`${props.id}-dropdown-toggle-input`}
						className={clsx({
							field: true,
							option: true,
							disabled: props.disabled,
						})}
						onChange={(event) => {
							handleQueryChange(event);

							props.onChange({
								target: { id: event.target.id, value: event.target.value },
								type: "type",
								isTrusted: false,
							});
						}}
						onClick={() => {
							if (!isActive && items?.length > 0) {
								setIsActive(true);
							}
						}}
						value={query}
					/>
				) : (
					<button
						type="button"
						aria-haspopup="listbox"
						aria-expanded={isActive}
						data-testid={`${props.id}-dropdown-toggle`}
						className={clsx({
							field: true,
							"drop-button": true,
							"with-options": items?.length > 0,
							option: true,
							active: isActive,
							disabled: props.disabled,
						})}
						onClick={handleExpandDropdown}
					>
						{selectedItemRef.current?.key}
					</button>
				)}

				<ul
					aria-activedescendant={accessibilitySubject}
					className={clsx({
						options: true,
						hidden: !isActive,
					})}
					onKeyDown={handleKeyDown}
					data-testid={`${props.id}-dropdown-options`}
					role="listbox"
					tabIndex={props.disabled || isActive || items?.length === 0 ? -1 : 0}
					aria-labelledby={props.id}
				>
					{props?.isFetching ? (
						<li
							className={clsx({
								option: true,
								loading: true,
							})}
						>
							<Loading />
						</li>
					) : (
						items?.map((item, index) => {
							const uid = `${accessibilitySubject}-${item.key}-${index}`;

							return (
								<li
									key={uid}
									id={uid}
									className={clsx({
										option: true,
										selected: item.isSelected,
										active: item.isActive,
										disabled: props.disabled,
									})}
									data-index={item.itemIndex}
									data-active={item.isActive}
									data-selected={item.isSelected}
									data-value={item.value}
									data-testid={`${item.key}-option`}
									onMouseOver={() => {
										changeItemProperty({
											item,
											property: ITEM_PROPERTIES.IS_ACTIVE,
										});
									}}
									onClick={() => handleItemClick(item)}
									onKeyDown={handleItemKeyDown}
									role="option"
									aria-selected={item.isActive}
									tabIndex={isActive ? 0 : -1}
								>
									{renderOptionContent(item)}
								</li>
							);
						})
					)}
				</ul>
				{props.error && (
					<span
						className="errorMessage"
						data-automationid={`${props.id}.error`}
						data-testid={`${props.id}.error`}
					>
						<FormattedMessage
							id={props.error}
							values={{ fieldname: props.label }}
						/>
					</span>
				)}
			</div>
		);
	},
);
